import { supabase } from '@/lib/supabase';
import {
  RepairJob,
  CustomerDevice,
  RepairPayment,
  PaginatedResult,
} from '@/types/models';
import { PAGE_SIZE } from '@/lib/constants';

export interface FetchRepairsParams {
  search?: string;
  status?: string;
  priority?: string;
  page?: number;
  isStaff?: boolean;
}

export async function fetchRepairs(
  params: FetchRepairsParams
): Promise<PaginatedResult<RepairJob>> {
  const { search = '', status = '', priority = '', page = 0, isStaff = false } = params;
  const from = page * PAGE_SIZE;
  const to = from + PAGE_SIZE - 1;

  if (isStaff) {
    const { data: rpcData, error: rpcErr } = await supabase.rpc('get_repairs_catalog');
    if (!rpcErr && rpcData) {
      let filtered = (rpcData as any[]) || [];
      if (search.trim()) {
        const s = search.toLowerCase().trim();
        filtered = filtered.filter(
          (item) =>
            item.job_number?.toLowerCase().includes(s) ||
            item.customer_name?.toLowerCase().includes(s) ||
            item.device_label?.toLowerCase().includes(s) ||
            item.serial_number?.toLowerCase().includes(s) ||
            item.reported_issue?.toLowerCase().includes(s)
        );
      }
      if (status) filtered = filtered.filter((i) => i.status === status);
      if (priority) filtered = filtered.filter((i) => i.priority === priority);

      const pageData = filtered.slice(from, from + PAGE_SIZE);
      const mapped = pageData.map((item) => ({
        id: item.id,
        job_number: item.job_number,
        customer_id: item.customer_id,
        customer: { id: item.customer_id, name: item.customer_name } as any,
        reported_issue: item.reported_issue,
        status: item.status,
        priority: item.priority,
        received_at: item.received_at,
        estimated_completion: item.estimated_completion,
        estimated_charge: 0,
        parts_cost: 0,
        parts_charge: 0,
        labour_charge: 0,
        total_charge: item.total_charge,
        amount_paid: item.amount_paid,
        balance_due: item.balance_due,
        assigned_to: item.assigned_to,
        customer_device: {
          brand: item.device_label,
          model: '',
          serial_number: item.serial_number,
        } as any,
      }));
      return { data: mapped as RepairJob[], count: filtered.length };
    }
  }

  let query = supabase
    .from('repair_jobs')
    .select(
      '*, customer:customers(id, name, phone), customer_device:customer_devices(*), inventory_laptop:laptops(id, stock_id, brand, model, serial_number), assigned_profile:profiles!repair_jobs_assigned_to_fkey(id, full_name)',
      { count: 'exact' }
    )
    .order('received_at', { ascending: false })
    .range(from, to);

  if (search.trim()) {
    const s = search.trim();
    query = query.or(
      `job_number.ilike.%${s}%,reported_issue.ilike.%${s}%,diagnosis.ilike.%${s}%,work_performed.ilike.%${s}%`
    );
  }

  if (status) query = query.eq('status', status);
  if (priority) query = query.eq('priority', priority);

  const { data, error, count } = await query;
  if (error) throw error;

  return { data: (data ?? []) as RepairJob[], count: count ?? 0 };
}

export async function fetchRepairById(id: string, isStaff = false): Promise<RepairJob> {
  if (isStaff) {
    const { data: safeData, error: safeErr } = await supabase.rpc('get_repair_detail_safe', {
      p_repair_id: id,
    });
    if (!safeErr && safeData) {
      const s = safeData as any;
      const r = s.repair;
      return {
        ...r,
        customer: s.customer,
        customer_device: s.device?.source === 'customer_device' ? s.device : null,
        inventory_laptop: s.device?.source === 'inventory_laptop' ? s.device : null,
        parts: s.parts,
      } as RepairJob;
    }
  }

  const { data, error } = await supabase
    .from('repair_jobs')
    .select(
      '*, customer:customers(id, name, phone, email, address), customer_device:customer_devices(*), inventory_laptop:laptops(*), assigned_profile:profiles!repair_jobs_assigned_to_fkey(id, full_name), parts:repair_parts(*, accessory:accessories(id, name, sku))'
    )
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as RepairJob;
}

export async function fetchCustomerDevices(customerId: string): Promise<CustomerDevice[]> {
  const { data, error } = await supabase
    .from('customer_devices')
    .select('*')
    .eq('customer_id', customerId)
    .order('created_at', { ascending: false });

  if (error) return [];
  return (data ?? []) as CustomerDevice[];
}

export async function createCustomerDevice(payload: {
  customer_id: string;
  brand: string;
  model: string;
  serial_number?: string;
  cpu?: string;
  ram?: string;
  storage?: string;
  colour?: string;
  charger_received?: boolean;
  accessories_received?: string;
  intake_condition?: string;
  login_password_hint?: string;
  notes?: string;
}): Promise<CustomerDevice> {
  const { data, error } = await supabase
    .from('customer_devices')
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data as CustomerDevice;
}

export interface CreateRepairJobPayload {
  customer_id: string;
  reported_issue: string;
  customer_device_id?: string | null;
  inventory_laptop_id?: string | null;
  priority?: string;
  estimated_completion?: string | null;
  estimated_charge?: number;
  intake_notes?: string | null;
  assigned_to?: string | null;
  accessories_received?: string[];
  estimated_labour?: number;
  estimated_parts?: number;
  before_photos?: string[];
}

export async function createRepairJobRpc(payload: CreateRepairJobPayload): Promise<{
  repair_id: string;
  job_number: string;
}> {
  const { data, error } = await supabase.rpc('create_repair_job', {
    p_customer_id: payload.customer_id,
    p_reported_issue: payload.reported_issue,
    p_customer_device_id: payload.customer_device_id ?? null,
    p_inventory_laptop_id: payload.inventory_laptop_id ?? null,
    p_priority: payload.priority ?? 'normal',
    p_estimated_completion: payload.estimated_completion ?? null,
    p_estimated_charge: payload.estimated_charge ?? 0,
    p_intake_notes: payload.intake_notes ?? null,
    p_assigned_to: payload.assigned_to ?? null,
    p_accessories_received: payload.accessories_received ?? [],
    p_estimated_labour: payload.estimated_labour ?? 0,
    p_estimated_parts: payload.estimated_parts ?? 0,
    p_before_photos: payload.before_photos ?? [],
  });

  if (error) throw error;
  return data as { repair_id: string; job_number: string };
}

export interface UpdateRepairJobSafePayload {
  repair_id: string;
  status?: string | null;
  diagnosis?: string | null;
  work_performed?: string | null;
  priority?: string | null;
  estimated_completion?: string | null;
  labour_charge?: number | null;
  assigned_to?: string | null;
  customer_approval_status?: string | null;
  approval_notes?: string | null;
  after_photos?: string[] | null;
}

export async function updateRepairJobSafeRpc(payload: UpdateRepairJobSafePayload): Promise<{
  repair_id: string;
  job_number: string;
  status: string;
  priority: string;
  total_charge: number;
  amount_paid: number;
  balance_due: number;
}> {
  const { data, error } = await supabase.rpc('update_repair_job_safe', {
    p_repair_id: payload.repair_id,
    p_status: payload.status ?? null,
    p_diagnosis: payload.diagnosis ?? null,
    p_work_performed: payload.work_performed ?? null,
    p_priority: payload.priority ?? null,
    p_estimated_completion: payload.estimated_completion ?? null,
    p_labour_charge: payload.labour_charge ?? null,
    p_assigned_to: payload.assigned_to ?? null,
    p_customer_approval_status: payload.customer_approval_status ?? null,
    p_approval_notes: payload.approval_notes ?? null,
    p_after_photos: payload.after_photos ?? null,
  });

  if (error) throw error;
  return data as any;
}

export interface AddRepairPartPayload {
  repair_id: string;
  accessory_id?: string | null;
  description?: string | null;
  quantity?: number;
  unit_cost?: number | null;
  unit_charge?: number | null;
}

export async function addRepairPartRpc(payload: AddRepairPartPayload): Promise<{ success: boolean; part_id: string }> {
  const { data, error } = await supabase.rpc('add_repair_part', {
    p_repair_id: payload.repair_id,
    p_accessory_id: payload.accessory_id ?? null,
    p_description: payload.description ?? null,
    p_quantity: payload.quantity ?? 1,
    p_unit_cost: payload.unit_cost ?? null,
    p_unit_charge: payload.unit_charge ?? null,
  });

  if (error) throw error;
  return data as any;
}

export async function removeRepairPartRpc(partId: string): Promise<{ success: boolean }> {
  const { data, error } = await supabase.rpc('remove_repair_part', {
    p_part_id: partId,
  });

  if (error) throw error;
  return data as any;
}

export async function recordRepairCollectionRpc(payload: {
  repair_id: string;
  collection_notes?: string | null;
  signature?: string | null;
}): Promise<{ success: boolean; collected_at: string }> {
  const { data, error } = await supabase.rpc('record_repair_collection', {
    p_repair_id: payload.repair_id,
    p_collection_notes: payload.collection_notes ?? null,
    p_signature: payload.signature ?? null,
  });

  if (error) throw error;
  return data as any;
}

export async function fetchRepairTimeline(repairId: string): Promise<any[]> {
  const { data, error } = await supabase
    .from('repair_activity_timeline')
    .select('*, profile:profiles(full_name)')
    .eq('repair_id', repairId)
    .order('created_at', { ascending: true });

  if (error) return [];
  return data ?? [];
}

export interface RecordRepairPaymentPayload {
  repair_id: string;
  amount: number;
  payment_method?: string;
  reference?: string | null;
  notes?: string | null;
}

export async function recordRepairPaymentRpc(payload: RecordRepairPaymentPayload): Promise<void> {
  const { error } = await supabase.rpc('record_repair_payment', {
    p_repair_id: payload.repair_id,
    p_amount: payload.amount,
    p_payment_method: payload.payment_method ?? 'cash',
    p_reference: payload.reference ?? null,
    p_notes: payload.notes ?? null,
  });

  if (error) throw error;
}

export async function fetchRepairPayments(repairId: string): Promise<RepairPayment[]> {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('repair_id', repairId)
    .eq('direction', 'in')
    .order('payment_date', { ascending: false });

  if (error) return [];
  return (data ?? []) as RepairPayment[];
}

export async function fetchStaffProfiles(): Promise<Array<{ id: string; full_name: string }>> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name')
    .order('full_name');

  if (error) return [];
  return (data ?? []) as Array<{ id: string; full_name: string }>;
}

